import { apiService } from "./apiService";
import type {
  User,
  AuthTokens,
  LoginCredentials,
  RegisterCredentials,
  BuyerRegisterCredentials,
  BuyerRegisterResponse,
} from "../types";

const AUTH_ENDPOINTS = {
  login: "/auth/login",
  buyerLogin: "/api/v2/buyers/auth/login",
  register: "/auth/register",
  buyerRegister: "/api/v2/buyers/auth/register",
  googleAuth: "/api/v2/buyers/auth/google-auth",
  logout: "/auth/logout",
  refresh: "/auth/refresh",
  me: "/auth/me",
  buyerProfile: "/api/buyer/profile", // New endpoint
} as const;

export const authService = {
  /**
   * Login user with email and password (legacy endpoint)
   */
  async login(
    credentials: LoginCredentials
  ): Promise<{ user: User; tokens: AuthTokens }> {
    const url = AUTH_ENDPOINTS.login;
    const response = await apiService.post<{ user: User; tokens: AuthTokens }>(
      url,
      credentials as unknown as Record<string, unknown>
    );
    return response;
  },

  /**
   * Login buyer with email and password (v2 endpoint)
   */
  async loginBuyer(
    credentials: LoginCredentials
  ): Promise<{ user: User; tokens: AuthTokens }> {
    const url = AUTH_ENDPOINTS.buyerLogin;
    const response = await apiService.post<{
      success: boolean;
      message: string;
      data: {
        user: User;
        token: string;
      };
    }>(url, credentials as unknown as Record<string, unknown>);

    return {
      user: response.data.user,
      tokens: {
        accessToken: response.data.token,
        refreshToken: response.data.token, // TODO: API doesn't provide separate refresh token
        tokenType: "Bearer",
      },
    };
  },

  /**
   * Register new user
   */
  async register(
    credentials: RegisterCredentials
  ): Promise<{ user: User; tokens: AuthTokens }> {
    const url = AUTH_ENDPOINTS.register;
    const response = await apiService.post<{ user: User; tokens: AuthTokens }>(
      url,
      credentials as unknown as Record<string, unknown>
    );
    return response;
  },

  /**
   * Authenticate with Google OAuth
   */
  async googleAuth(idToken: string): Promise<{ user: User; tokens: AuthTokens }> {
    const url = AUTH_ENDPOINTS.googleAuth;
    const response = await apiService.post<{
      success: boolean;
      message: string;
      data: {
        user: User;
        token: string;
      };
    }>(url, { token: idToken });

    return {
      user: response.data.user,
      tokens: {
        accessToken: response.data.token,
        refreshToken: response.data.token,
        tokenType: "Bearer",
      },
    };
  },

  /**
   * Register new buyer with complete profile
   */
  async registerBuyer(
    credentials: BuyerRegisterCredentials
  ): Promise<BuyerRegisterResponse> {
    const url = AUTH_ENDPOINTS.buyerRegister;
    const response = await apiService.post<{
      success: boolean;
      message: string;
      data: {
        user: User;
        token: string;
      };
    }>(url, credentials as unknown as Record<string, unknown>);

    // Transform API response to match BuyerRegisterResponse
    return {
      success: response.success,
      message: response.message,
      data: {
        user: response.data.user,
        token: response.data.token,
      },
      pagination: null,
      user: response.data.user,
      tokens: {
        accessToken: response.data.token,
        refreshToken: response.data.token,
        tokenType: "Bearer",
      },
    };
  },

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    try {
      const url = AUTH_ENDPOINTS.logout;
      await apiService.post<void>(url);
    } catch (error) {
      // Even if logout fails, we should clear local storage
      console.warn("Logout request failed:", error);
    }
  },

  /**
   * Refresh access token
   */
  async refreshToken(refreshToken: string): Promise<AuthTokens> {
    const url = AUTH_ENDPOINTS.refresh;
    const response = await apiService.post<AuthTokens>(url, { refreshToken });
    return response;
  },

  /**
   * Get current user profile (legacy endpoint)
   * @deprecated Use getBuyerProfile() for buyer accounts
   */
  async getCurrentUser(): Promise<User> {
    const url = AUTH_ENDPOINTS.me;
    const response = await apiService.get<User>(url);
    return response;
  },

  /**
   * Get complete buyer profile with all details
   * This is the primary method for fetching authenticated buyer data
   */
  async getBuyerProfile(): Promise<User> {
    const url = AUTH_ENDPOINTS.buyerProfile;
    const response = await apiService.get<User>(url);
    return response;
  },

  /**
   * Update user profile
   */
  async updateProfile(userData: Partial<User>): Promise<User> {
    const url = AUTH_ENDPOINTS.me;
    const response = await apiService.put<User>(
      url,
      userData as unknown as Record<string, unknown>
    );
    return response;
  },

  /**
   * Update buyer profile
   */
  async updateBuyerProfile(userData: Partial<User>): Promise<User> {
    const url = AUTH_ENDPOINTS.buyerProfile;
    const response = await apiService.put<User>(
      url,
      userData as unknown as Record<string, unknown>
    );
    return response;
  },
};

export default authService;
